import React, { useState } from 'react';
import { SHEET_DEPT_META, DEFAULT_PRICE_TABLE, getTableCols } from '../../config/constants';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn, inp } from '../../config/styles';

export function PriceTableView({ priceTable, onUpdate }) {
  const { territories } = useTenantConfig();
  const TABLE_COLS = getTableCols(territories);
  const [editCell, setEditCell] = useState(null); // { gbp, col }
  const [editVal,  setEditVal]  = useState('');
  const [newGbp,   setNewGbp]   = useState('');

  const sortedKeys = Object.keys(priceTable).map(Number).sort((a, b) => a - b);

  const startEdit = (gbp, col) => {
    setEditCell({ gbp, col });
    setEditVal(String(priceTable[gbp][TABLE_COLS.indexOf(col)] ?? 0));
  };

  const commitEdit = () => {
    if (!editCell) return;
    const { gbp, col } = editCell;
    const idx = TABLE_COLS.indexOf(col);
    const newRow = [...priceTable[gbp]];
    newRow[idx] = parseFloat(editVal) || 0;
    onUpdate({ ...priceTable, [gbp]: newRow });
    setEditCell(null);
  };

  const addRow = () => {
    const gbp = parseFloat(newGbp);
    if (!gbp || priceTable[gbp]) return;
    const defaultRow = TABLE_COLS.map(() => 0);
    onUpdate({ ...priceTable, [gbp]: defaultRow });
    setNewGbp('');
  };

  const removeRow = (gbp) => {
    const next = { ...priceTable };
    delete next[gbp];
    onUpdate(next);
  };

  const resetToDefaults = () => onUpdate(
    Object.fromEntries(Object.entries(DEFAULT_PRICE_TABLE).map(([gbp, row]) => [
      gbp, TABLE_COLS.map((_, i) => row[i] ?? 0),
    ]))
  );

  const thS = {
    padding: '7px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11,
    color: C.muted, background: C.fa, borderBottom: `2px solid ${C.d0}`,
    whiteSpace: 'nowrap',
  };
  const tdS = (active) => ({
    padding: 0, borderBottom: `1px solid ${C.f4}`, textAlign: 'right',
    background: active ? '#fffdf0' : C.white,
    outline: active ? `2px solid #f59e0b` : 'none', outlineOffset: -2,
    cursor: 'text', minWidth: 72,
  });

  return (
    <div style={{ padding: '28px 32px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ fontFamily: F.display, fontSize: 30, color: C.text }}>Price Table</div>
        <button style={btn('danger')} onClick={resetToDefaults}>Reset to defaults</button>
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
        GBP price points and their converted equivalents. Click any cell to edit. Changes apply immediately to all territory pricing calculations.
      </div>

      <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${C.e8}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: F.body }}>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: 'left', background: SHEET_DEPT_META.ops.hdr, color: SHEET_DEPT_META.ops.fg }}>GBP (£)</th>
              {TABLE_COLS.map(col => (
                <th key={col} style={{ ...thS, background: SHEET_DEPT_META.trading.hdr, color: SHEET_DEPT_META.trading.fg }}>{col}</th>
              ))}
              <th style={{ ...thS, width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {sortedKeys.map(gbp => (
              <tr key={gbp}>
                <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.f4}`, fontFamily: F.mono, fontSize: 12, fontWeight: 600, color: C.text, background: C.fa, minWidth: 80 }}>
                  £{gbp}
                </td>
                {TABLE_COLS.map(col => {
                  const isEdit = editCell?.gbp === gbp && editCell?.col === col;
                  const val = priceTable[gbp][TABLE_COLS.indexOf(col)] ?? 0;
                  return (
                    <td key={col} style={tdS(isEdit)} onClick={() => !isEdit && startEdit(gbp, col)}>
                      {isEdit ? (
                        <input
                          autoFocus
                          type="number"
                          value={editVal}
                          onChange={e => setEditVal(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); commitEdit(); } if (e.key === 'Escape') setEditCell(null); }}
                          style={{ width: '100%', height: 34, border: 'none', background: 'transparent', fontFamily: F.mono, fontSize: 12, color: C.text, padding: '0 10px', textAlign: 'right', outline: 'none' }}
                        />
                      ) : (
                        <div style={{ padding: '0 10px', height: 34, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: F.mono }}>
                          {val}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={{ padding: '0 8px', borderBottom: `1px solid ${C.f4}`, textAlign: 'center', background: C.fa }}>
                  <button onClick={() => removeRow(gbp)} title="Remove row" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
        <input
          type="number"
          value={newGbp}
          onChange={e => setNewGbp(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addRow()}
          placeholder="New GBP price point (e.g. 155)"
          style={{ ...inp({ width: 260 }) }}
        />
        <button style={btn('secondary')} onClick={addRow}>+ Add price point</button>
      </div>
    </div>
  );
}
